import type { TrainingTeamReportItem } from "@/api/client";

/**
 * Three-state Level 1 Certification vocabulary (deals-gap W2).
 *
 * - `certified`      — every compulsory module is complete (green).
 * - `never_started`  — zero training activity ever (`has_started === false`),
 *                      so the deal-registration gate applies (red).
 * - `unfinished`     — some activity but compulsory training incomplete, gate
 *                      still applies (amber).
 *
 * Shared by the CM/PO/RM/RA team report and the Admin Hub users directory so
 * the badge wording/colour can never drift between surfaces. The RACCPE-23
 * exemption badge is a fourth, overlay state rendered alongside — an exempt
 * BDM still reads never_started/unfinished here.
 */
export type CertificationState = "certified" | "never_started" | "unfinished";

export function deriveCertification(item: TrainingTeamReportItem): CertificationState {
  const compulsory = item.modules.filter((m) => m.is_compulsory);
  // Vacuously certified when nothing is compulsory — there is no Level 1
  // requirement to be behind on.
  if (compulsory.every((m) => m.status === "completed")) return "certified";
  return item.has_started ? "unfinished" : "never_started";
}

/**
 * CHP-840: canonical per-person certification labels for the Insights cert
 * columns. Unifies the old PO Insights vocabulary ("Not started" / "In
 * progress") and the admin "— gated" vocabulary into a single set, derived
 * from the CHP-838 program-mode card labels (in sentence case per CHP-839).
 *
 * Scoped to the Insights drawers (TeamTrainingDrawer + PartnerDeepDiveDrawer);
 * the shared CERTIFICATION_BADGE below keeps its colour/variant mapping and is
 * still used elsewhere (e.g. the Admin Hub) untouched.
 */
export const CERTIFICATION_STATUS_LABEL: Record<CertificationState, string> = {
  certified: "Fully certified",
  never_started: "Uncertified",
  unfinished: "In progress",
};

export const CERTIFICATION_BADGE: Record<
  CertificationState,
  { label: string; variant: "success" | "destructive" | "outline"; className?: string }
> = {
  certified: { label: "Certified", variant: "success" },
  never_started: { label: "Never started — gated", variant: "destructive" },
  // No amber Badge variant exists; tint an outline badge instead of widening
  // the shared ui component from this lane.
  unfinished: {
    label: "Unfinished — gated",
    variant: "outline",
    className: "border-amber-300 bg-amber-100 text-amber-900",
  },
};
